import { Tooltip } from "@mui/material";
import { AxiosError, AxiosResponse } from "axios";
import React, { useCallback, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { toast } from "react-toastify";
import {
  DownloadTemplate,
  GetFileTypes,
  UploadFile,
} from "Services/PGXLis/ResultUpload";
import PermissionComponent, {
  getPermissionDisplayName,
} from "Shared/Common/Permissions/PermissionComponent";
import useLang from "Shared/hooks/useLanguage";
import { LoaderIcon } from "Shared/Icons";

interface FileType {
  templateId: number;
  templateName: string;
  translatorId: string;
  translatorName: string;
}

const allowedExtensions = ["csv", "xls", "xlsx"];

function UploadHeader({ loadGridData }: any) {
  const { t } = useLang();

  const [files, setFiles] = useState<File[]>([]);
  const [resultType, setResultType] = useState<number>(0);
  const [invalidFileFormat, setInvalidFileFormat] = useState<boolean>(false);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0];
      if (!selectedFile) return;

      const fileExtension = selectedFile.name.split(".").pop()?.toLowerCase();

      if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
        setInvalidFileFormat(true);
      } else {
        setInvalidFileFormat(false);
      }

      setFiles((prev) => [...prev, selectedFile]);
      e.target.value = "";
    },
    []
  );

  const uploadMutation = useMutation<AxiosResponse, AxiosError, FormData>(
    (formData: FormData) => UploadFile(formData),
    {
      onSuccess: (res) => {
        if (res?.data?.statusCode === 200) {
          toast.success(t(res?.data?.message));
        } else if (res?.data?.statusCode === 400) {
          toast.error(t(res?.data?.message));
        }

        loadGridData(true, true);
        setFiles([]);
        setInvalidFileFormat(false);
        setResultType(0);
      },
      onError: (error) => {
        console.error(error);
      },
    }
  );

  const handleUpload = () => {
    if (files.length === 0) return;

    if (!resultType) {
      toast.error(t("Please Select File Type"));
      return;
    }

    const formData = new FormData();
    formData.append("file", files[0]);
    formData.append("TemplateId", resultType.toString());

    uploadMutation.mutate(formData);
  };

  const handleFileDeselect = useCallback((file: File) => {
    setFiles((prev) => prev.filter((f) => f.name !== file.name));
    setInvalidFileFormat(false);
  }, []);

  const handleResultTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setResultType(Number(e.target.value));
  };

  const { data: fileTypeList = [] } = useQuery<
    AxiosResponse<{ data: FileType[] }>,
    AxiosError,
    FileType[]
  >(["fileTypes", 11], () => GetFileTypes(11), {
    select: (res) => res.data.data,
    staleTime: Infinity,
  });

  const downloadMutation = useMutation<AxiosResponse, AxiosError, number>({
    mutationFn: (resultType: number) => DownloadTemplate(resultType),
    onSuccess: (res) => {
      const contentType = res?.data?.data?.contentType;
      const fileContents = res?.data?.data?.fileContents;

      const downloadLink = document.createElement("a");
      downloadLink.href = `data:${contentType};base64,${fileContents}`;
      downloadLink.download = "PGX File Upload Template.xlsx";
      downloadLink.click();
    },
    onError: (error) => {
      console.error(error);
    },
  });

  const handleDownload = () => {
    if (!resultType) {
      toast.error(t("Please Select File Type"));
      return;
    }

    downloadMutation.mutate(resultType);
  };

  return (
    <div className="gap-4 px-4 py-3 mb-3 rounded bg-white">
      <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
        <div className="d-flex align-items-center gap-4 justify-content-start flex-wrap mb-4">
          <span className="fw-bold">{t("File Type:")}</span>

          <label className="d-flex justify-content-start align-items-start flex-wrap gap-3">
            {fileTypeList.map((fileType) => (
              <div key={fileType.templateId}>
                <input
                  id={`IDResultFileFileTypeRadioButton_${fileType.templateId}`}
                  className="form-check-input w-20px h-20px"
                  type="radio"
                  value={fileType.templateId}
                  name="resultType"
                  checked={resultType === fileType.templateId}
                  onChange={handleResultTypeChange}
                />
                <span className="ps-2 mr-2">{`${fileType.templateName} - ${fileType.translatorName}`}</span>
              </div>
            ))}
          </label>
        </div>

        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex gap-2">
            <PermissionComponent
              moduleName="PGX LIS"
              pageName="Result File"
              permissionIdentifier="FileUpload"
            >
              <input
                id="IDResultFileUploadFileInput"
                type="file"
                onChange={handleFileSelect}
                className="d-none"
                disabled={files.length > 0}
                accept=".csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              />

              <label
                htmlFor="IDResultFileUploadFileInput"
                className="dropzoneBulk py-1 px-8 d-flex align-items-center rounded cursor-pointer"
              >
                <div className="dz-message needsclick">{t("Choose File")}</div>
              </label>

              <Tooltip title={t("Upload")} arrow placement="top">
                <button
                  id="BulkPatientUploadUoloadButton"
                  className="btn btn-icon btn-sm fw-bold btn-upload btn-icon-light"
                  onClick={handleUpload}
                  disabled={files.length > 0 ? uploadMutation.isLoading : true}
                >
                  {uploadMutation.isLoading ? (
                    <LoaderIcon />
                  ) : (
                    <i className="bi bi-cloud-upload" />
                  )}
                </button>
              </Tooltip>
            </PermissionComponent>
          </div>

          <div>
            <PermissionComponent
              moduleName="PGX LIS"
              pageName="Result File"
              permissionIdentifier="DownloadTemplate"
            >
              {({ pageName, moduleName, permissionIdentifier }) => (
                <button
                  className="btn btn-warning btn-sm fw-bold btn-download"
                  onClick={handleDownload}
                  disabled={downloadMutation.isLoading}
                >
                  <i className="fa fa-download"></i>
                  <span>
                    {getPermissionDisplayName(
                      pageName,
                      moduleName,
                      permissionIdentifier,
                      "Download Template"
                    )}
                  </span>
                </button>
              )}
            </PermissionComponent>
          </div>
        </div>

        {files.length > 0 && (
          <div className="mt-3">
            {files.map((file) => (
              <div
                key={file.name}
                className="border bg-light-secondary rounded p-2 mb-2"
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div
                    className="text-dark-65 text-truncate"
                    style={{ maxWidth: "50%" }}
                    title={file.name}
                  >
                    {file.name}
                  </div>
                  <span
                    style={{ fontSize: "13px", cursor: "pointer" }}
                    onClick={() => handleFileDeselect(file)}
                  >
                    &#x2716;
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {invalidFileFormat && (
          <div className="text-dark-65 form__error mt-2">
            {t("Invalid file format. Please choose a CSV, XLS, or XLSX file.")}
          </div>
        )}
      </div>
    </div>
  );
}

export default React.memo(UploadHeader);
